import { useMutation, useApolloClient, useQuery } from '@apollo/client'
import { ReactElement, useEffect, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

import FormLayoutV2 from 'components/v2/FormLayout'
import FormLayout from 'components/v2/FormLayout'

import Heading from 'components/v2/Heading'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { resolvers as loginResolvers } from 'resolvers/login'
import { canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import {
  CHANGE_BUSINESS_CLIENT_KREDIT_SETTINGS,
  CREATE_BUSINESS_CLIENT
} from 'features/BusinessClients/mutations-legacy'
import { NewBusinessClient } from 'features/BusinessClients/components/NewBusinessClient'
import { INewBusinessClientFormValues } from 'features/BusinessClients/types'
import { GTCompanySuggestionsResponse } from 'gql/types.generated'
import { useNavigate } from 'react-router-dom'
import { Loader } from '@10xLabs/react-web/components/Loader'
import EmptyState from 'components/v2/EmptyState'
import { Button } from 'components/v2/Button'
import { usePushFileToS3 } from 'utils/file'
import {
  DEFAULT_KREDITS_SETTINGS_VALUES,
  businessClientFormUtils,
  getInitialBusinessClientValues,
  mapFormValuesToKreditSettingsMutation
} from 'features/BusinessClients/utils'
import { isEmpty } from 'lodash'
import { canSelectCompany } from 'features/Login/utils'
import { getDifference } from 'utils/object'
import { GET_COMPANIES_SUGGESTIONS } from 'features/Companies/queries-legacy'

const messages = defineMessages({
  helmet: {
    id: 'companyClients.new.helmet',
    defaultMessage: 'New Company Client | Nexbus Admin'
  }
})

const NewBusinessClientView = (): ReactElement => {
  const intl = useIntl()
  const client = useApolloClient()
  const login = useLogin()

  client.addResolvers(loginResolvers)
  let content: ReactElement
  const navigate = useNavigate()

  const [createClient, { loading, error }] = useMutation<{ createBusinessClient: { id: string } }>(
    CREATE_BUSINESS_CLIENT
  )
  const [changeKreditSettings, { loading: loadingChangeKreditSettings, error: errorChangeKreditSettings }] =
    useMutation(CHANGE_BUSINESS_CLIENT_KREDIT_SETTINGS)
  const companyId = !canSelectCompany(login.accessToken) && login.accessToken.companyId

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch
  } = useQuery<{ companySuggestions: GTCompanySuggestionsResponse }>(GET_COMPANIES_SUGGESTIONS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !companyId,
    variables: {
      id: companyId
    }
  })
  const pushFileToS3 = usePushFileToS3()
  const [s3Error, setS3Error] = useState<string | undefined>()
  const [s3Loading, setS3Loading] = useState<boolean>(false)

  const uploadLogo = async (file: File): Promise<string> => {
    try {
      setS3Loading(true)
      const path = await pushFileToS3(file)
      setS3Loading(false)
      return path
    } catch (error) {
      // @ts-expect-error FIXME
      setS3Error(error.message)
      setS3Loading(false)
      // @ts-expect-error FIXME
      throw new Error(error)
    }
  }
  const handleSubmit = async (client: INewBusinessClientFormValues): Promise<void> => {
    const kreditSettingsPayload = getDifference(
      client.kreditsSettings.categories,
      DEFAULT_KREDITS_SETTINGS_VALUES.categories
    )

    const { mapFormToCreateMutation } = businessClientFormUtils
    try {
      let path = ''
      if (!isEmpty(client.logoFile)) {
        path = await uploadLogo(client.logoFile as File)

        client.logo = {
          path,
          name: (client.logoFile as File).name
        }
      }
      const clientInput = mapFormToCreateMutation(client)

      const { data } = await createClient({
        variables: {
          businessClient: clientInput
        }
      })

      if (!isEmpty(kreditSettingsPayload)) {
        await changeKreditSettings({
          variables: {
            input: mapFormValuesToKreditSettingsMutation(
              client.kreditsSettings,
              data?.createBusinessClient.id as string
            )
          }
        })
      }
      navigate('/business-clients')
    } catch (error) {
      //do nothing
    }
  }

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canWrite(FEATURES.BUSINESS_CLIENT, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.BUSINESS_CLIENT} />
  }
  if (queryLoading) {
    content = (
      <FormLayout.Content>
        <FormLayout.Card>
          <EmptyState.Container>
            <Loader />
          </EmptyState.Container>
        </FormLayout.Card>
      </FormLayout.Content>
    )
  } else if (queryError) {
    content = (
      <FormLayout.Content>
        <FormLayout.Card>
          <EmptyState.Container>
            <EmptyState.Description>
              <FormattedMessage
                id="businessClients.edit.error.description"
                defaultMessage="There was an error while loading the company"
              />
            </EmptyState.Description>
            <EmptyState.Action>
              <Button
                onClick={(): void => {
                  refetch()
                }}
                kind="primary"
              >
                <FormattedMessage id="businessClients.edit.error.action" defaultMessage="Reload" />
              </Button>
            </EmptyState.Action>
          </EmptyState.Container>
        </FormLayout.Card>
      </FormLayout.Content>
    )
  } else {
    content = (
      <FormLayout.Content>
        <Heading.H3>
          <FormattedMessage id="companyClients.new.title" defaultMessage="Add business client" />
        </Heading.H3>
        <NewBusinessClient
          onSubmit={handleSubmit}
          loading={loading || s3Loading || loadingChangeKreditSettings}
          error={error || s3Error || errorChangeKreditSettings}
          businessClient={getInitialBusinessClientValues(queryData?.companySuggestions?.items.at(0))}
        />
      </FormLayout.Content>
    )
  }
  return <FormLayoutV2.Container>{content}</FormLayoutV2.Container>
}

export default withLastUrl(NewBusinessClientView)
